/**
 * GoogleAnalyticsHelper to track events with Firebase Analytics (gtag)
 */
class GoogleAnalyticsHelper {
  constructor() {
    this.isInitialized = false;
    this.debugMode = this.isDebugMode();
    this.checkGtagAvailability();
  }

  /**
   * Check if debug mode is enabled
   */
  isDebugMode() {
    return window.chatwootConfig?.responzaDebug === 'true';
  }

  /**
   * Check if gtag is available
   */
  checkGtagAvailability() {
    if (typeof window !== 'undefined' && window.gtag) {
      this.isInitialized = true;
    }
  }

  /**
   * Track page view
   * @param {string} pagePath - The path of the page
   * @param {string} pageTitle - The title of the page
   * @param {Object} additionalParams - Additional parameters to send
   */
  trackPageView(pagePath, pageTitle, additionalParams = {}) {
    if (!this.isInitialized) {
      this.checkGtagAvailability();
    }

    // Re-check debug mode in case it changed
    this.debugMode = this.isDebugMode();

    if (this.isInitialized && window.gtag) {
      const eventData = {
        page_path: pagePath,
        page_title: pageTitle,
        ...additionalParams
      };
      
      if (this.debugMode) {
        console.log('🔥 Google Analytics: Tracking page view', {
          path: pagePath,
          title: pageTitle,
          params: additionalParams,
          fullEventData: eventData
        });
      }
      
      window.gtag('event', 'page_view', eventData);
    } else if (this.debugMode) {
      console.warn('⚠️ Google Analytics: gtag not available', {
        isInitialized: this.isInitialized,
        gtagExists: !!window.gtag
      });
    }
  }

  /**
   * Track custom event
   * @param {string} eventName - The name of the event
   * @param {Object} parameters - Event parameters
   */
  trackEvent(eventName, parameters = {}) {
    if (!this.isInitialized) {
      this.checkGtagAvailability();
    }

    this.debugMode = this.isDebugMode();

    if (this.isInitialized && window.gtag) {
      if (this.debugMode) {
        console.log('🔥 Google Analytics: Tracking event', {
          eventName,
          parameters
        });
      }
      
      window.gtag('event', eventName, parameters);
    } else if (this.debugMode) {
      console.warn('⚠️ Google Analytics: Cannot track event - gtag not available', {
        eventName,
        parameters
      });
    }
  }

  /**
   * Set user properties
   * @param {Object} properties - User properties
   */
  setUserProperties(properties) {
    if (!this.isInitialized) {
      this.checkGtagAvailability();
    }

    this.debugMode = this.isDebugMode();

    if (this.isInitialized && window.gtag) {
      if (this.debugMode) {
        console.log('🔥 Google Analytics: Setting user properties', properties);
      }
      
      window.gtag('set', 'user_properties', properties);
    } else if (this.debugMode) {
      console.warn('⚠️ Google Analytics: Cannot set user properties - gtag not available', properties);
    }
  }

  /**
   * Set user ID for tracking
   * @param {string} userId - The user ID
   */
  setUserId(userId) {
    if (!this.isInitialized) {
      this.checkGtagAvailability();
    }

    this.debugMode = this.isDebugMode();

    if (this.isInitialized && window.gtag) {
      if (this.debugMode) {
        console.log('🔥 Google Analytics: Setting user ID', { userId });
      }
      
      window.gtag('config', 'G-LKZ99BMTKF', {
        user_id: userId
      });
    } else if (this.debugMode) {
      console.warn('⚠️ Google Analytics: Cannot set user ID - gtag not available', { userId });
    }
  }
}

export default new GoogleAnalyticsHelper();